import { container } from 'tsyringe'
import Vue from 'vue'
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule,
} from 'vuex-module-decorators'
import dayjs from 'dayjs'
import store from '@/app/ui/store'
import { Pagination } from '@/domain/entities/Pagination'
import {
  PROGRAMMATIC_VOUCHER_PAGINATION,
  PROGRAMMATIC_VOUCHER_HISTORY_PAGINATION,
} from '@/app/infrastructures/misc/Constants'
import {
  EventBus,
  EventBusConstants,
  LocalStorage,
  Utils,
} from '@/app/infrastructures/misc'
import {
  ProgrammaticVoucher,
  ProgrammaticVouchers,
  ProgrammaticVoucherHistory,
  ProgrammaticVoucherHistories,
  VoucherDistribution,
  VoucherDistributions,
} from '@/domain/entities/ProgrammaticVoucher'
import { ProgrammaticVoucherPresenter } from '@/app/ui/presenters/ProgrammaticVoucherPresenter'
import {
  CreateProgrammaticVoucherRequest,
  DeleteProgrammaticVoucherRequest,
  UpdateProgrammaticVoucherRequest,
  UploadProgrammaticVoucherRequest,
} from '@/data/payload/api/ProgrammaticVoucherRequest'
import { Trigger } from '@/domain/entities/Program'

export interface ProgrammaticVoucherState {
  isLoading: boolean
  isDownloading: boolean
  isUploading: boolean
  isLoadingRoute: boolean
  isLoadingTrigger: boolean
  voucherData: ProgrammaticVoucher[]
  voucherDetail: ProgrammaticVoucher
  paginationData: Pagination
  triggerData: Record<string, any>[]
  historyData: ProgrammaticVoucherHistory[]
  paginationHistory: Pagination
  distributionData: VoucherDistribution[]
  paginationDistribution: Pagination
}

@Module({
  namespaced: true,
  store,
  name: 'programmatic-voucher',
  dynamic: true,
})
class ProgrammaticVoucherController extends VuexModule
  implements ProgrammaticVoucherState {
  private presenter: ProgrammaticVoucherPresenter = container.resolve(
    ProgrammaticVoucherPresenter
  )
  public isLoading = false
  public isDownloading = false
  public isUploading = false
  public isLoadingRoute = false
  public isLoadingTrigger = false
  public voucherData = [new ProgrammaticVoucher()]
  public voucherDetail = new ProgrammaticVoucher()
  public paginationData = new Pagination(1, PROGRAMMATIC_VOUCHER_PAGINATION, 0)
  public historyData = [new ProgrammaticVoucherHistory()]
  public paginationHistory = new Pagination(
    1,
    PROGRAMMATIC_VOUCHER_HISTORY_PAGINATION,
    0
  )
  public distributionData = [new VoucherDistribution()]
  public paginationDistribution = new Pagination(
    1,
    PROGRAMMATIC_VOUCHER_HISTORY_PAGINATION,
    0
  )
  public triggerData = [{ label: '', value: '' }]
  public constants = {
    targetOptions: [
      { label: 'All Login Users', value: 'ALL_LOGIN_USERS' },
      { label: 'Selected Users', value: 'SELECTED_USERS' },
      { label: 'Specific OS', value: 'SPECIFIC_OS' },
      {
        label: 'Specific Origin',
        value: 'SPECIFIC_ORIGIN',
      },
      {
        label: 'Membership Level',
        value: 'MEMBERSHIP_LEVEL',
      },
    ],
    platformOSOptions: [
      {
        label: 'iOS',
        value: 'IOS',
      },
      {
        label: 'Android',
        value: 'ANDROID',
      },
    ],
    measurementOptions: [
      {
        label: 'Greater Than',
        value: 'GREATER_THAN',
      },
      {
        label: 'Lower Than',
        value: 'LOWER_THAN',
      },
      {
        label: 'Equal',
        value: 'EQUAL',
      },
    ],
    membershipLevel: [
      {
        label: 'Basic',
        value: 'Basic',
      },
      {
        label: 'Reguler',
        value: 'Regular',
      },
      {
        label: 'Premium',
        value: 'Premium',
      },
    ],
    levelUpOptions: [
      {
        label: 'Basic To Reguler',
        value: 'Basic-Regular',
      },
      {
        label: 'Reguler To Premium',
        value: 'Regular-Premium',
      },
    ],
    purposeOptions: [
      {
        label: 'Payment',
        value: 'PAYMENT',
      },
      {
        label: 'Redeem To Point',
        value: 'REDEEM_TO_POINT',
      },
    ],
    tncOptions: [
      {
        label: 'Redeem To Point',
        value: 'REDEEM_TO_POINT',
      },
      {
        label: 'Discount By Amount',
        value: 'DISCOUNT_BY_AMOUNT',
      },
      {
        label: 'Discount By Percentage',
        value: 'DISCOUNT_BY_PERCENTAGE',
      },
      {
        label: 'Cashback',
        value: 'CASHBACK',
      },
    ],
    pointPurposes: [
      {
        label: 'Pickup Reguler',
        value: 'PICKUP',
      },
      {
        label: 'Drop paket Reguler',
        value: 'DROPOFF',
      },
      {
        label: 'Pickup Favorit',
        value: 'PICKUP_FAVORITE',
      },
      {
        label: 'Drop paket Favorit',
        value: 'DROPOFF_FAVORITE',
      },
      {
        label: 'BisaBelanja',
        value: 'SHOP',
      },
    ],
  }

  @Action({ rawError: true })
  public getProgrammaticVoucherList(params: Record<string, any>) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(params),
      'snake_case'
    )

    this.presenter
      .getAll(formattedParams)
      .then(res => {
        this.setProgrammaticVoucherData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Programmatic Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getProgrammaticVoucherDetail(publicVoucherId: string) {
    this.setLoading(true)

    this.presenter
      .get(publicVoucherId)
      .then(res => {
        this.setProgrammaticVoucherDetail(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Programmatic Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public createProgrammaticVoucher(form: {
    voucherName: string
    voucherTarget: Record<string, any>
    selectedUserFile: File
    os: Record<string, any>
    osValidation: Record<string, any>
    osValidationVersion: string
    routeIds: Record<string, any>[]
    membershipLevel: Record<string, any>
    voucherTrigger: Record<string, any>
    scheduleDate: string
    scheduleTime: string
    membershipLevelUp: Record<string, any>
    limitPerDay: number
    triggerInvoiceType: string[]
    voucherPurposeParent: Record<string, any>
    voucherPurposeChild: Record<string, any>
    pointAmount: number
    pointExpiry: number
    pointPurpose: string[]
    discountAmount: number
    discountMinimum: number
    discountPercentage: number
    discountPercentageMinimum: number
    discountPercentageMaximum: number
    cashbackPercentage: number
    cashbackPercentageMinimum: number
    cashbackPercentageMaximum: number
    voucherPurposeInvoiceType: string[]
    expiryDay: number
    limitUsage: number
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    budgetAmount: null | number | undefined
  }) {
    this.setLoading(true)
    const adminEmail = LocalStorage.getLocalStorage(
      LocalStorage.LOGIN_IDENTITY,
      true
    )

    const payload = new CreateProgrammaticVoucherRequest(
      form.voucherName,
      form.voucherTarget.value,
      undefined,
      undefined,
      undefined,
      form.os.value,
      form.osValidation.value,
      form.osValidationVersion,
      form.routeIds.map(item => item.value),
      form.membershipLevel.value,
      form.voucherTrigger.value,
      form.scheduleDate && form.scheduleTime
        ? dayjs(
            `${form.scheduleDate} ${form.scheduleTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format()
        : undefined,
      form.membershipLevelUp.value,
      form.limitPerDay,
      form.triggerInvoiceType.join(','),
      form.voucherPurposeParent.value,
      form.voucherPurposeChild.value,
      form.pointAmount,
      form.pointExpiry,
      form.pointPurpose.join(','),
      form.discountAmount,
      form.discountMinimum,
      form.discountPercentage,
      form.discountPercentageMinimum,
      form.discountPercentageMaximum,
      form.cashbackPercentage,
      form.cashbackPercentageMinimum,
      form.cashbackPercentageMaximum,
      form.voucherPurposeInvoiceType.join(','),
      form.expiryDay,
      form.limitUsage,
      dayjs(
        `${form.startDate} ${form.startTime}`,
        'YYYY-MM-DD HH:mm:ss'
      ).format(),
      dayjs(`${form.endDate} ${form.endTime}`, 'YYYY-MM-DD HH:mm:ss').format(),
      adminEmail,
      form.budgetAmount === undefined ? null : form.budgetAmount,
    )

    if (form.voucherTarget.value === 'SELECTED_USERS') {
      this.presenter
        .uploadFile(new UploadProgrammaticVoucherRequest(form.selectedUserFile))
        .then(data => {
          payload.selectedUserUrl = data.url
          payload.selectedUser = data.selectedUser
          payload.selectedUserFileName = data.fileName

          this.onCreateProgramVoucher(payload)
        })
        .catch(error => {
          this.setLoading(false)
          Vue.notify({
            title: 'Upload User File Failed',
            text:
              error.status === 400 || error.status === 422
                ? error.error.message.en
                : 'Something wrong',
            type: 'error',
            duration: 9000,
          })
        })
        .finally(() => {
          this.setUploading(false)
        })
    } else {
      this.onCreateProgramVoucher(payload)
    }
  }

  @Action({ rawError: true })
  onCreateProgramVoucher(payload: CreateProgrammaticVoucherRequest) {
    this.presenter
      .create(payload)
      .then(() => {
        EventBus.$emit(EventBusConstants.CREATE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Create Programmatic Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public updateProgrammaticVoucher(form: {
    voucherId: string
    voucherName: string
    voucherTarget: Record<string, any>
    selectedUser: string
    selectedUserFileUrl: string
    selectedUserFileName: string
    os: Record<string, any>
    osValidation: Record<string, any>
    osValidationVersion: string
    routeIds: Record<string, any>[]
    membershipLevel: Record<string, any>
    voucherTrigger: Record<string, any>
    scheduleDate: string
    scheduleTime: string
    membershipLevelUp: Record<string, any>
    limitPerDay: number
    triggerInvoiceType: string[]
    voucherPurposeParent: Record<string, any>
    voucherPurposeChild: Record<string, any>
    pointAmount: number
    pointExpiry: number
    pointPurpose: string[]
    discountAmount: number
    discountMinimum: number
    discountPercentage: number
    discountPercentageMinimum: number
    discountPercentageMaximum: number
    cashbackPercentage: number
    cashbackPercentageMinimum: number
    cashbackPercentageMaximum: number
    voucherPurposeInvoiceType: string[]
    expiryDay: number
    limitUsage: number
    startDate: string
    startTime: string
    endDate: string
    endTime: string
    budgetAmount: null | number | undefined
  }) {
    this.setLoading(true)
    const adminEmail = LocalStorage.getLocalStorage(
      LocalStorage.LOGIN_IDENTITY,
      true
    )

    const payload = new UpdateProgrammaticVoucherRequest(
      form.voucherTarget.value,
      form.selectedUser,
      form.selectedUserFileUrl,
      form.selectedUserFileName,
      form.os.value,
      form.osValidation.value,
      form.osValidationVersion,
      form.routeIds.map(item => item.value),
      form.membershipLevel.value,
      form.voucherTrigger.value,
      form.scheduleDate && form.scheduleTime
        ? dayjs(
            `${form.scheduleDate} ${form.scheduleTime}`,
            'YYYY-MM-DD HH:mm:ss'
          ).format()
        : undefined,
      form.membershipLevelUp.value,
      form.limitPerDay,
      form.triggerInvoiceType.join(','),
      form.voucherPurposeParent.value,
      form.voucherPurposeChild.value,
      form.pointAmount,
      form.pointExpiry,
      form.pointPurpose.join(','),
      form.discountAmount,
      form.discountMinimum,
      form.discountPercentage,
      form.discountPercentageMinimum,
      form.discountPercentageMaximum,
      form.cashbackPercentage,
      form.cashbackPercentageMinimum,
      form.cashbackPercentageMaximum,
      form.voucherPurposeInvoiceType.join(','),
      form.expiryDay,
      form.limitUsage,
      dayjs(
        `${form.startDate} ${form.startTime}`,
        'YYYY-MM-DD HH:mm:ss'
      ).format(),
      dayjs(`${form.endDate} ${form.endTime}`, 'YYYY-MM-DD HH:mm:ss').format(),
      adminEmail,
      form.budgetAmount === undefined ? null : form.budgetAmount,
    )

    this.presenter
      .update(form.voucherId, payload)
      .then(() => {
        EventBus.$emit(EventBusConstants.UPDATE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Update Programmatic Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public deleteVoucher(form: { voucherId: string }) {
    this.setLoading(true)

    const payload = new DeleteProgrammaticVoucherRequest(
      LocalStorage.getLocalStorage(LocalStorage.LOGIN_IDENTITY, true)
    )

    this.presenter
      .delete(form.voucherId, payload)
      .then(() => {
        EventBus.$emit(EventBusConstants.DELETE_VOUCHER_SUCCESS, {})
      })
      .catch(error => {
        Vue.notify({
          title: 'Delete Programmatic Voucher Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getVoucherDistributions(form: {
    id: string
    params: Record<string, any>
  }) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(form.params),
      'snake_case'
    )

    this.presenter
      .getVoucherDistributions(form.id, formattedParams)
      .then(res => {
        this.setVoucherDistribution(res)
      })
      .catch(error => {
        this.setVoucherDistribution(
          new VoucherDistributions(this.paginationDistribution, [])
        )
        Vue.notify({
          title: 'Fetch Voucher Distribution Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getVoucherHistory(form: { id: string; params: Record<string, any> }) {
    this.setLoading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(form.params),
      'snake_case'
    )

    this.presenter
      .getHistories(form.id, formattedParams)
      .then(res => {
        this.setVoucherHistory(res)
      })
      .catch(error => {
        this.setVoucherHistory(
          new ProgrammaticVoucherHistories(this.paginationHistory, [
            new ProgrammaticVoucherHistory(),
          ])
        )
        Vue.notify({
          title: 'Fetch History Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  @Action({ rawError: true })
  public getExportedFile(form: { id: string; params: Record<string, any> }) {
    this.setDownloading(true)
    const formattedParams = Utils.toInstance(
      new Map(),
      JSON.stringify(form.params),
      'snake_case'
    )

    this.presenter
      .exportHistories(form.id, formattedParams)
      .then(res => {
        EventBus.$emit(EventBusConstants.DOWNLOAD_VOUCHER_SUCCESS, {
          downloadUrl: res,
        })
      })
      .catch(error => {
        Vue.notify({
          title: 'Download Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setDownloading(false)
      })
  }

  @Action({ rawError: true })
  public getTriggers() {
    this.setTriggerLoading(true)

    this.presenter
      .getTriggers()
      .then(res => {
        this.setTriggerData(res)
      })
      .catch(error => {
        Vue.notify({
          title: 'Fetch Trigger List Failed',
          text:
            error.status === 400 || error.status === 422
              ? error.error.message.en
              : 'Something wrong',
          type: 'error',
          duration: 5000,
        })
      })
      .finally(() => {
        this.setTriggerLoading(false)
      })
  }

  @Mutation
  private setVoucherDistribution(distributions: VoucherDistributions) {
    this.paginationDistribution = distributions.pagination as Pagination
    this.distributionData = distributions.data as VoucherDistribution[]
  }

  @Mutation
  private setVoucherHistory(histories: ProgrammaticVoucherHistories) {
    this.paginationHistory = histories.pagination as Pagination
    this.historyData = histories.data as ProgrammaticVoucherHistory[]
  }

  @Mutation
  private setTriggerData(triggers: Trigger[]) {
    this.triggerData = triggers.map(item => ({
      label: item.triggerName,
      value: item.triggerId,
    }))
  }

  @Mutation
  private setProgrammaticVoucherData(publicVoichers: ProgrammaticVouchers) {
    this.paginationData = publicVoichers.pagination as Pagination
    this.voucherData = publicVoichers.data as ProgrammaticVoucher[]
  }

  @Mutation
  private setProgrammaticVoucherDetail(publicVoucher: ProgrammaticVoucher) {
    this.voucherDetail = publicVoucher
  }

  @Mutation
  private setDownloading(bool: boolean) {
    this.isDownloading = bool
  }

  @Mutation
  private setUploading(bool: boolean) {
    this.isUploading = bool
  }

  @Mutation
  private setLoading(bool: boolean) {
    this.isLoading = bool
  }

  @Mutation
  private setRouteLoading(bool: boolean) {
    this.isLoadingRoute = bool
  }

  @Mutation
  private setTriggerLoading(bool: boolean) {
    this.isLoadingTrigger = bool
  }
}

export default getModule(ProgrammaticVoucherController)
